import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, getAuthToken, setAuthToken, ApiError } from './api';

/**
 * Auth state for RF-CRM.
 *
 * Token is in sessionStorage (cleared on tab close — acceptable for internal
 * CRM; switch to localStorage in Phase 5+ if "remember me" is requested).
 *
 * On mount with an existing token, we verify via /api/auth/me. If that 401s,
 * the token is cleared and user lands on /login.
 */

export type Role = 'MANUFACTURER' | 'INTEGRATOR';

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  integratorId: string | null;
  integrator?: { id: string; name: string } | null;
  /** Issue #66 — «Менеджер» flag (from /auth/me); drives the leads-tab filters. */
  isManager?: boolean;
}

interface LoginResponse {
  token: string;
  user: User;
}

interface AuthContextValue {
  user: User | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  /** On mount: if a token is present, fetch /me to confirm and hydrate user. */
  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setStatus('unauthenticated');
      return;
    }
    let cancelled = false;
    api
      .get<User>('/auth/me')
      .then((u) => {
        if (cancelled) return;
        setUser(u);
        setStatus('authenticated');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 401 already clears the token in api.ts; just transition state.
        if (err instanceof ApiError && err.status === 401) {
          setUser(null);
        }
        setStatus('unauthenticated');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<LoginResponse>('/auth/login', { email, password });
    setAuthToken(res.token);
    setUser(res.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(() => {
    setAuthToken(null);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, login, logout }),
    [user, status, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

/** Convenience: is the current user a manufacturer? Returns false if not authed. */
// eslint-disable-next-line react-refresh/only-export-components
export function useIsManufacturer(): boolean {
  const { user } = useAuth();
  return user?.role === 'MANUFACTURER';
}
